import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { MentionConfig } from 'angular-mentions';
import { FieldDef } from 'app/core/models';
import { Pipeline } from 'app/modules/projects/projects.types';
import { WidgetsModule } from 'app/shared/components/widgets/widgets.module';
import { CustomFormOptions } from 'app/shared/modules/custom-form/custom-form.types';
import { PipesModule } from 'app/shared/pipes/pipes.module';
import { MentionsService } from 'app/shared/services/mentions.service';

@Component({
  selector: 'app-add-deal-params-form',
  templateUrl: './add-deal-params-form.component.html',
  styleUrls: ['./add-deal-params-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    PipesModule,
    WidgetsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDealParamsFormComponent extends OnDestroyMixin {
  @Input() showAddFieldOption = false;
  @Input() pipelines: Pipeline[];
  @Input() set customFields(value: FieldDef[]) {
    this._customFields = value;
    this.fieldNameControl.setValidators(this._customFields ? [Validators.required] : null);
    this.fieldNameControl.updateValueAndValidity();
  }
  @Input() customFormData: CustomFormOptions;
  @Output() close = new EventEmitter();

  mentionConfig: MentionConfig;

  private _customFields: FieldDef[];
  private customFieldPanelTemplatePortal: TemplatePortal;
  private customFieldPanelOverlayRef: OverlayRef;

  @ViewChild('customFieldPanel', { static: true }) private customFieldPanel: TemplateRef<any>;
  @ViewChild('customFieldPanelOrigin') private customFieldPanelOrigin: any;

  private get fieldNameControl(): AbstractControl {
    return this.form.get('field_name');
  }

  get customFields(): FieldDef[] {
    return this._customFields;
  }

  form = new FormGroup({
    pipeline_id: new FormControl('', [Validators.required]),
    field_name: new FormControl(''),
  });
  fieldSearchValue = '';

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly mentionsService: MentionsService,
  ) {
    super();
    this.mentionConfig = this.mentionsService.getFieldMentionConfig();
  }

  openAddFieldForm(): void {
    this.customFieldPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.customFieldPanelOrigin.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    this.customFieldPanelOverlayRef
      .attachments()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.customFieldPanelOverlayRef.overlayElement.querySelector('input').focus());

    this.customFieldPanelTemplatePortal = new TemplatePortal(this.customFieldPanel, this.viewContainerRef);

    this.customFieldPanelOverlayRef.attach(this.customFieldPanelTemplatePortal);
    this.customFieldPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.closeCustomFieldPanel());
  }

  private closeCustomFieldPanel(): void {
    if (this.customFieldPanelTemplatePortal && this.customFieldPanelTemplatePortal.isAttached) {
      this.customFieldPanelTemplatePortal.detach();
    }

    if (this.customFieldPanelOverlayRef && this.customFieldPanelOverlayRef.hasAttached()) {
      this.customFieldPanelOverlayRef.detach();
      this.customFieldPanelOverlayRef.dispose();
    }
  }

  onFieldAdded(fieldDef: FieldDef): void {
    this._customFields = [fieldDef];

    this.fieldNameControl.setValue(fieldDef.name);
    this.fieldNameControl.updateValueAndValidity();
    this.closeCustomFieldPanel();

    this.changeDetectorRef.detectChanges();
  }
}
